import { useQuery } from '@tanstack/react-query';
import { fetchAddressPredictions, retryAddressLookup, type AddressPrediction } from '@services/addressLookupService';

type Args = {
  searchTerm: string;
  /** A container's id when the search is scoped to one, otherwise null. */
  containerId: string | null;
  sessionToken: string;
  enabled: boolean;
};

type UsePredictionsQueryResult = {
  data: AddressPrediction[] | undefined;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
};

export const usePredictionsQuery = ({
  searchTerm,
  containerId,
  sessionToken,
  enabled,
}: Args): UsePredictionsQueryResult => {
  const query = useQuery<AddressPrediction[], Error>({
    // `containerId` is part of the key, not just the request: without it a
    // container's children and an unscoped search for the same term share one
    // cache entry, and whichever ran first is served to the other.
    //
    // `sessionToken` deliberately is not. It changes every episode, so keying on
    // it would miss the cache on every repeat of a term the customer has already
    // searched — which is the one protection left against the provider's
    // search-to-resolution ratio monitoring.
    queryKey: ['addressPredictions', searchTerm, containerId ?? ''],
    queryFn: () => fetchAddressPredictions({ searchTerm, containerId: containerId ?? undefined, sessionToken }),
    enabled,
    retry: retryAddressLookup,
    // Five minutes: customers type a street, back out and come back to it, and
    // every repeat inside this window is free. With rate limiting out of scope
    // this cache is the only remaining protection against the provider's
    // prediction-to-resolution ratio monitoring.
    staleTime: 300_000,
    // Neither of these is something a customer did. A tab regaining focus or a
    // connection coming back would otherwise each spend a search against the
    // account's allowance, and a search nobody asked for never leads to a sale.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    data: query.data,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
  };
};
